import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { DashboardOverview, LiveStats, ApiResponse } from '@/types/api';
import { USE_MOCKS } from '@/lib/mockConfig';
import { mockOverview, mockLiveStats } from '../components/mockData';

/**
 * The API's /dashboard/overview and /analytics/performance return a nested
 * `{ metrics, integrations, breakdown, trends, ... }` shape that does NOT match
 * the flat DashboardOverview the UI consumes. These adapters reconcile the two
 * so real data drops straight into the existing charts/cards.
 */
function adaptOverview(ov: any, perf: any): DashboardOverview {
  const leads = ov?.metrics?.leads || {};
  const integrations = ov?.integrations || {};
  const leadsByPlatform: Array<{ platform: string; count: number }> = ov?.breakdown?.leadsByPlatform || [];
  const platformTotal = leadsByPlatform.reduce((sum, p) => sum + (p.count || 0), 0);
  const recentLeads: any[] = ov?.recentActivity?.leads || [];
  const recentCrm: any[] = ov?.recentActivity?.crmUpdates || [];
  const trends: any[] = perf?.trends?.leads || [];
  const avgResponse = parseFloat(String(perf?.responseTime?.averageResponseTime || '')) || 0;

  return {
    totalLeads: leads.total || 0,
    activeLeads: leads.inProgress || 0,
    conversionRate: parseFloat(leads.conversionRate) || 0,
    responseTime: avgResponse,
    connectedAccounts: {
      meta: integrations.metaAccounts || 0,
      ecommerce: integrations.ecommerceConnections || 0,
      total: (integrations.metaAccounts || 0) + (integrations.ecommerceConnections || 0),
    },
    recentActivity: [
      ...recentLeads.map((l, i) => ({
        id: l.id || `lead-${i}`,
        type: 'lead',
        message: l.customerName ? `New lead: ${l.customerName}` : l.message || 'New lead',
        timestamp: l.createdAt || new Date().toISOString(),
      })),
      ...recentCrm.map((c, i) => ({
        id: c.id || `crm-${i}`,
        type: 'crm',
        message: c.customerName ? `Profile updated: ${c.customerName}` : 'CRM profile updated',
        timestamp: c.lastInteraction || c.updatedAt || new Date().toISOString(),
      })),
    ].slice(0, 8),
    leadsOverTime: trends.map((t) => ({
      date: String(t.period),
      count: t.totalLeads || 0,
      converted: t.convertedLeads || 0,
    })),
    platformDistribution: leadsByPlatform.map((p) => ({
      platform: p.platform,
      count: p.count || 0,
      percentage: platformTotal ? Math.round((p.count / platformTotal) * 100) : 0,
    })),
  };
}

function adaptLiveStats(ls: any): LiveStats {
  const alerts = ls?.alerts || {};
  const raw = ls?.health?.systemStatus;
  const systemHealth: LiveStats['systemHealth'] =
    raw === 'warning' ? 'warning' : raw === 'error' ? 'error' : 'healthy';
  return {
    activeConnections: alerts.activeSyncs || 0,
    messagesLastHour: alerts.recentCrmActivity || 0,
    pendingLeads: alerts.pendingLeads || 0,
    systemHealth,
  };
}

export const useDashboard = () => {
  // Overview: merges /overview (metrics, integrations, breakdown, recent) with
  // /analytics/performance (time series + response time) into one shape.
  const overviewQuery = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: async () => {
      const [ovRes, perfRes] = await Promise.all([
        apiClient.get<ApiResponse<any>>('/api/dashboard/overview'),
        apiClient.get<ApiResponse<any>>('/api/dashboard/analytics/performance').catch(() => null),
      ]);
      const ov = ovRes.data.data || {};
      const perf = perfRes?.data?.data || {};
      const totalLeads = ov?.metrics?.leads?.total || 0;
      // Empty DB → show demo data so the dashboard is reviewable.
      if (USE_MOCKS && totalLeads === 0) return mockOverview;
      return adaptOverview(ov, perf);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });

  // Live stats (more frequent updates)
  const liveStatsQuery = useQuery({
    queryKey: ['dashboard', 'live-stats'],
    queryFn: () =>
      apiClient.get<ApiResponse<any>>('/api/dashboard/stats/live')
        .then(res => {
          const ls = res.data.data || {};
          const alerts = ls.alerts || {};
          const anyActivity =
            (alerts.newLeads || 0) + (alerts.pendingLeads || 0) + (alerts.recentCrmActivity || 0) + (alerts.activeSyncs || 0);
          if (USE_MOCKS && anyActivity === 0) return mockLiveStats;
          return adaptLiveStats(ls);
        })
        .catch((err) => {
          if (USE_MOCKS) return mockLiveStats;
          throw err;
        }),
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 15 * 1000, // Refetch every 15 seconds
  });

  return {
    overview: overviewQuery.data,
    liveStats: liveStatsQuery.data,
    isLoading: overviewQuery.isLoading || liveStatsQuery.isLoading,
    error: overviewQuery.error || liveStatsQuery.error,
  };
};

export const useQuickActions = () => {
  // Static shortcuts. ⚠️ No counts in the copy: these used to say "12 leads waiting" and
  // "5 unresolved conversations" to every tenant, whatever their real numbers were.
  const quickActions = [
    {
      id: '1',
      title: 'View Pending Leads',
      description: 'Leads waiting for a response',
      icon: 'Users',
      href: '/leads?status=pending',
      color: 'orange'
    },
    {
      id: '2',
      title: 'Connect Facebook Page',
      description: 'Add a new page to automate',
      icon: 'Facebook',
      href: '/integrations?tab=meta',
      color: 'blue'
    },
    {
      id: '3',
      title: 'Review Conversations',
      description: 'Conversations that still need a reply',
      icon: 'MessageSquare',
      href: '/conversations?status=pending',
      color: 'green'
    },
    {
      id: '4',
      title: 'Check Analytics',
      description: 'View performance metrics',
      icon: 'BarChart3',
      href: '/analytics',
      color: 'purple'
    }
  ];

  return { quickActions };
};